import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ZoomInIcon, XIcon } from 'lucide-react';
export function MariinskyTheatre() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  return <div className="w-full min-h-screen bg-white" data-id="element-1338">
      {/* Image Modal */}
      {selectedImage && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" onClick={() => setSelectedImage(null)} data-id="element-1339">
          <button onClick={() => setSelectedImage(null)} className="absolute top-4 right-4 text-white hover:text-emerald-400 transition-colors" data-id="element-1340">
            <XIcon className="w-8 h-8" data-id="element-1341" />
          </button>
          <motion.img initial={{
        scale: 0.9
      }} animate={{
        scale: 1
      }} src={selectedImage} alt="Enlarged view" className="max-w-full max-h-full object-contain" data-id="element-1342" />
        </motion.div>}
      {/* Header */}
      <header className="py-8 px-6 bg-white border-b border-stone-200 sticky top-0 z-40" data-id="element-1343">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-1344">
          <Link to="/architecture" className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors" data-id="element-1345">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-1346" />
            <span className="font-light" data-id="element-1347">Back to Architecture</span>
          </Link>
          <h1 className="text-2xl font-light text-stone-900" data-id="element-1348">Maria Elina</h1>
        </div>
      </header>
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-stone-50 via-emerald-50/30 to-stone-50" data-id="element-1349">
        <div className="max-w-5xl mx-auto" data-id="element-1350">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-1351">
            <p className="text-emerald-600 font-light tracking-wide uppercase mb-4" data-id="element-1352">
              Interior Planning, Adaptive Re-Use, Cultural Architecture
            </p>
            <h1 className="text-4xl md:text-6xl font-light text-stone-900 mb-6" data-id="element-1353">
              Mariinsky Theatre-3
            </h1>
            <p className="text-xl text-stone-600 font-light mb-8" data-id="element-1354">
              Transforming St. Petersburg's historic Main Post Office into a
              world-class concert venue and cultural center
            </p>
            <div className="space-y-4 text-stone-700 font-light" data-id="element-1355">
              <p className="text-lg" data-id="element-1356">
                <span className="font-normal text-stone-900" data-id="element-1357">Role:</span>{' '}
                Interior Planning, Project Coordination
              </p>
              <p className="text-lg" data-id="element-1358">
                <span className="font-normal text-stone-900" data-id="element-1359">
                  Collaboration:
                </span>{' '}
                Kleinewelt Architekten
              </p>
              <p className="text-lg" data-id="element-1360">
                <span className="font-normal text-stone-900" data-id="element-1361">Location:</span>{' '}
                Pochtamtskaya st., 9a, St. Petersburg, Russia
              </p>
              <p className="text-lg" data-id="element-1362">
                <span className="font-normal text-stone-900" data-id="element-1363">Type:</span>{' '}
                Cultural Architecture, Historic Preservation
              </p>
              <p className="text-lg" data-id="element-1364">
                <span className="font-normal text-stone-900" data-id="element-1365">Ideologist:</span>{' '}
                Valery Gergiev
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Project Overview */}
      <section className="py-20 px-6 bg-white" data-id="element-1366">
        <div className="max-w-5xl mx-auto" data-id="element-1367">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1368">
            <h2 className="text-3xl font-light text-stone-900 mb-6" data-id="element-1369">
              Project Vision
            </h2>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-1370">
              The proposition, created in collaboration with Kleinewelt
              Architekten, envisioned creating a "complementary center" between
              St Isaac's Square and New Holland - two of St. Petersburg's most
              significant cultural landmarks.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed" data-id="element-1371">
              The general concept, agreed upon with the city, involved
              comprehensive development of the territory with several cultural
              "anchors." Central to this vision was the transformation of the
              historic Main Post Office building (Glavpochtampt) into
              "Mariinsky-3" - a concert venue conceived as St. Petersburg's
              answer to London's Royal Albert Hall.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Courtyard Strategy */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1372">
        <div className="max-w-7xl mx-auto" data-id="element-1373">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1374">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1375">
              Courtyard Programming Strategy
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1376">
              The original concept envisaged six distinct courtyard spaces, each
              with its own character and programming. The design creates a
              network of interconnected cultural experiences, evoking the
              intimate scale and variety of Italian city streets.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/image%20(6).png')} data-id="element-1377">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1378">
                <img src="https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/image%20(6).png" alt="Courtyard Programming Diagram" className="w-full h-auto" data-id="element-1379" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1380">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1381" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1382">
                Six distinct courtyard zones: Main Courtyard (ceremonial
                entrance), Long Courtyard (classical promenade), Secret Garden
                (private art garden), Art Courtyard (bohemian art displays),
                Alleyway (urban street movement), and Room Courtyard (floor
                rotation)
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Building History */}
      <section className="py-20 px-6 bg-white" data-id="element-1383">
        <div className="max-w-7xl mx-auto" data-id="element-1384">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1385">
            <h2 className="text-3xl font-light text-stone-900 mb-6" data-id="element-1386">
              Building Study
            </h2>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-1387">
              The original structure was built in 1789 according to the design
              of renowned architect N. A. Lvov, serving as St. Petersburg's Head
              Post Office for over two centuries. The building represents a
              significant example of Russian Neoclassical architecture, with its
              characteristic symmetry, classical proportions, and elegant
              detailing.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-12" data-id="element-1388">
              In 2019, a comprehensive reconstruction project for the area was
              announced, opening the possibility for this historic structure to
              be reimagined for contemporary cultural use while preserving its
              architectural heritage and urban significance.
            </p>
            {/* Building Study Images */}
            <div className="space-y-8" data-id="element-1389">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/image%20(11).png')} data-id="element-1390">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1391">
                  <img src="https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/image%20(11).png" alt="Historical Building Documentation" className="w-full h-auto" data-id="element-1392" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1393">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1394" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1395">
                  Historical documentation showing the original courtyard spaces
                  and the re-imagined proposal
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/image%20(12).png')} data-id="element-1396">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1397">
                  <img src="https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/image%20(12).png" alt="Building Sections and Plans" className="w-full h-auto" data-id="element-1398" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1399">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1400" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1401">
                  Technical drawings showing the central theatre space and
                  interior courtyard organization
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Transforming Theatre Concept */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1402">
        <div className="max-w-5xl mx-auto" data-id="element-1403">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1404">
            <h2 className="text-3xl font-light text-stone-900 mb-6" data-id="element-1405">
              User Experience Exploration: The Transforming Theatre
            </h2>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-1406">
              In the ideology championed by Valery Gergiev, the crown element of
              the space was to be a "transforming theatre"—a flexible
              performance venue capable of adapting to different types of
              musical and theatrical presentations.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed" data-id="element-1407">
              Following extensive building studies, design attention focused on
              facilitating this transformative concept, creating a space that
              could seamlessly shift between intimate chamber music settings,
              full orchestral performances, and experimental theatrical
              productions while maintaining acoustic excellence and audience
              connection.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Stage Layout Experimentation */}
      <section className="py-20 px-6 bg-white" data-id="element-1408">
        <div className="max-w-7xl mx-auto" data-id="element-1409">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1410">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1411">
              Stage Configuration Studies
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1412">
              Extensive exploration of stage and seating configurations to
              optimize the transforming theatre concept. Each layout was
              analyzed for capacity, sightlines, acoustic performance, and
              adaptability to different performance types.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1413">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/image%20(14).png')} data-id="element-1414">
                <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-1415">
                  <img src="https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/image%20(14).png" alt="Stage Layout Experiments - Set 1" className="w-full h-auto" data-id="element-1416" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1417">
                    <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1418" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-sm" data-id="element-1419">
                  Study 1 with capacity ranging from 1,920 to 1,770 seats
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/image%20(15).png')} data-id="element-1420">
                <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-1421">
                  <img src="https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/image%20(15).png" alt="Stage Layout Experiments - Set 2" className="w-full h-auto" data-id="element-1422" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1423">
                    <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1424" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-sm" data-id="element-1425">
                  Study 2 with capacity ranging from 2,318 to 2,242 seats
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/image%20(16).png')} data-id="element-1426">
                <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-1427">
                  <img src="https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/image%20(16).png" alt="Stage Layout Experiments - Set 3" className="w-full h-auto" data-id="element-1428" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1429">
                    <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1430" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-sm" data-id="element-1431">
                  Advanced configurations with capacities from 1,636 to 1,442
                  seats
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Final Selected Layouts */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1432">
        <div className="max-w-7xl mx-auto" data-id="element-1433">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1434">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1435">
              Final Stage Layout Options
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1436">
              The design process culminated in eight refined layout options,
              each optimized for different performance scenarios while
              maintaining the transforming theatre concept. These final
              configurations balance capacity, acoustics, flexibility, and
              audience experience.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/image%20(13).png')} data-id="element-1437">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1438">
                <img src="https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/image%20(13).png" alt="Final Stage Layout Options" className="w-full h-auto" data-id="element-1439" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1440">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1441" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1442">
                Eight final stage configuration options demonstrating the
                transformable nature of the performance space
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* The Final Plans */}
      <section className="py-20 px-6 bg-white" data-id="element-1443">
        <div className="max-w-5xl mx-auto" data-id="element-1444">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1445">
            <h2 className="text-3xl font-light text-stone-900 mb-6" data-id="element-1446">
              Design Philosophy: Bohemian Eclectic
            </h2>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-1447">
              The selected style—Bohemian Eclectic—was chosen specifically to
              "bridge the old and the new," creating a design language that
              respects the building's neoclassical heritage while introducing
              contemporary cultural vitality.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed" data-id="element-1448">
              The design centered around arts and cultural activities,
              transforming the historic post office into a comprehensive
              cultural ecosystem with antique shops, artist workshops,
              contemporary art showrooms, co-working spaces, a boutique hostel,
              and the transforming theatre as its crown jewel.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Gallery Space */}
      <section className="py-20 px-6 bg-white" data-id="element-1449">
        <div className="max-w-7xl mx-auto" data-id="element-1450">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1451">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1452">
              Social Spaces: Gallery & Café
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1453">
              Besides the theatre, the design transformed the historic alleyway
              into a vibrant social space. The gallery and café areas feature
              elegant proportions, natural light, and carefully curated details
              that invite lingering and social interaction.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/62813248a00c90dc296e95d7be34367002fa6735_1164_770.jpg')} data-id="element-1454">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1455">
                <img src="https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/62813248a00c90dc296e95d7be34367002fa6735_1164_770.jpg" alt="Central Post Office Gallery" className="w-full h-auto" data-id="element-1456" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1457">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1458" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1459">
                Re-imagined gallery and café space with preserved architectural
                details
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Central Atrium */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1460">
        <div className="max-w-7xl mx-auto" data-id="element-1461">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1462">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1463">
              Blended Experience: The Central Atrium
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1464">
              Following inspiration from classical Italian architecture, the
              atrium was designed to facilitate theatrical action while serving
              as a grand social space. The dramatic glass roof floods the space
              with natural light, while preserved neoclassical columns create a
              dialogue between past and present.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/image%20(9).png')} data-id="element-1465">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1466">
                <img src="https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/image%20(9).png" alt="Central Atrium" className="w-full h-auto" data-id="element-1467" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1468">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1469" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1470">
                The central atrium with its dramatic glass roof, preserved
                columns, and flexible event space capable of hosting
                performances, exhibitions, and social gatherings
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Pedestrian Street */}
      <section className="py-20 px-6 bg-white" data-id="element-1471">
        <div className="max-w-7xl mx-auto" data-id="element-1472">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1473">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1474">
              The Italian Street: Pedestrian Zone Vision
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1475">
              The original concept envisaged the creation of a single pedestrian
              zone on Pochtamtskaya Street, filled with gardens, cafes,
              displays, and other means of attracting pass-through visitors. The
              content and landscaping were designed to evoke associations with
              the intimate streets of an Italian city.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/image%20(8).png')} data-id="element-1476">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1477">
                <img src="https://storage.yandexcloud.net/maria-images/images/mariinsky_ready/image%20(8).png" alt="Pedestrian Street Vision" className="w-full h-auto" data-id="element-1478" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1479">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1480" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1481">
                The pedestrian alleyway connecting courtyards, featuring
                landscaping, outdoor seating, and architectural bridges that
                create an Italian street atmosphere
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Project Impact */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1482">
        <div className="max-w-5xl mx-auto" data-id="element-1483">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1484">
            <h2 className="text-3xl font-light text-stone-900 mb-8" data-id="element-1485">
              Project Vision & Impact
            </h2>
            <div className="space-y-6 text-lg text-stone-600 font-light leading-relaxed" data-id="element-1486">
              <p data-id="element-1487">
                The Mariinsky Theatre-3 project represents a holistic approach
                to adaptive reuse, transforming St. Petersburg's historic Main
                Post Office into a vibrant cultural ecosystem. The design
                creates a "complementary center" between St Isaac's Square and
                New Holland, strengthening the city's cultural fabric.
              </p>
              <p data-id="element-1488">
                The transforming theatre concept—championed by Valery
                Gergiev—reimagines performance space as flexible and adaptable,
                capable of shifting between intimate chamber music, full
                orchestral performances, and experimental productions while
                maintaining acoustic excellence.
              </p>
              <p data-id="element-1489">
                By creating a permeable, multi-programmed destination with
                courtyards, galleries, workshops, and the Italian street
                atmosphere, the project invites the city in—making culture
                accessible, spontaneous, and integrated into daily life rather
                than confined to a fortress-like single-use building.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}